import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "components/button";
import Icon from "components/icon";
import ActionMenu from "components/shared/action-menu";
import { Columns, TableSortState } from "components/table";
import { useAppTranslation } from "locale/useAppTranslation";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { errorHandler } from "_services/errorHandler";
import { dateTimeFormatter } from "_helper/timeFormatter";
import {
  addCustomerPageRouteName,
  detailCustomerPageRouteName,
} from "modules/master-data/customer/constants/routes";
import {
  CustomerV1CustomerListItem, useCustomerServiceV1ListCustomersQuery, useCustomerServiceV1BulkUpdateCustomerStatusMutation, useCustomerServiceV1BulkDeleteCustomerMutation, useCustomerServiceV1ExportCustomersMutation, CustomerServiceV1BulkUpdateCustomerStatusApiArg as BulkUpdateCustomerStatusApiArg,
  CustomerServiceV1BulkDeleteCustomerApiArg as BulkDeleteCustomerApiArg,
  CustomerV1CustomerImportError,
  CustomerV1ExportCustomersReq,
  useCustomerServiceV1ImportCustomersMutation,
} from "_services/modules/customer";
import { CustomerRow } from "_interfaces/customer.interfaces";
import { env } from "config/env";

const sortByMap: Record<string, string> = {
  customerName: "customer_name",
  legalName: "legal_name",
  officialPhone: "phone",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

const normalizeStatus = (value?: string): "active" | "inactive" =>
  value === "active" ? "active" : "inactive";

const mapCustomerItemToRow = (item: CustomerV1CustomerListItem): CustomerRow => {
  const createdByName =
    typeof item.created_by === "object"
      ? item.created_by?.name || "-"
      : item.created_by || "-";

  const rawCreatedAt =
    typeof item.created_by === "object" && item.created_by?.timestamp
      ? item.created_by.timestamp
      : item.created_at;

  const formattedCreatedAt = rawCreatedAt ? dateTimeFormatter(rawCreatedAt) : "-";
  const createdAt = formattedCreatedAt === "—" ? "-" : formattedCreatedAt;

  const rawUpdatedByName =
    (typeof item.updated_by === "object"
      ? item.updated_by?.name
      : item.updated_by) || (createdByName !== "-" ? createdByName : undefined);

  const rawUpdatedAt =
    (typeof item.updated_by === "object" && item.updated_by?.timestamp
      ? item.updated_by.timestamp
      : item.updated_at) || rawCreatedAt;

  const formattedUpdatedAt = rawUpdatedAt ? dateTimeFormatter(rawUpdatedAt) : undefined;
  const updatedAt = formattedUpdatedAt === "—" ? undefined : formattedUpdatedAt;
  const updatedBy = rawUpdatedByName && rawUpdatedByName.trim() !== "" ? rawUpdatedByName : "-";

  return {
    id: item.id ?? "",
    CustomerName: item.customer_name ?? item.customer_code ?? item.id ?? "-",
    vendorCode: item.customer_code ?? "-",
    legalName: item.legal_name ?? "-",
    officialPhone: item.contact_person_phone_number ?? "-",
    registeredFleet: 0,
    status: normalizeStatus(item.status),
    createdBy: createdByName,
    createdAt: createdAt,
    updatedBy: updatedBy,
    updatedAt: updatedAt,
  };
};

const getBulkStatusUpdateSuccessMessage = (
  action: "active" | "inactive",
  count: number,
  getTranslation: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string => {
  if (action === "active") {
    if (count === 1) {
      return getTranslation(
        "bulk-activate-success-message",
        "1 Customer successfully activated",
        { count },
      );
    }
    return getTranslation(
      "bulk-activate-success-message_plural",
      "{{count}} Customers successfully activated",
      { count },
    );
  }

  if (count === 1) {
    return getTranslation(
      "bulk-deactivate-success-message",
      "1 Customer successfully deactivated",
      { count },
    );
  }
  return getTranslation(
    "bulk-deactivate-success-message_plural",
    "{{count}} Customers successfully deactivated",
    { count },
  );
};

const getBulkDeleteSuccessMessage = (
  count: number,
  getTranslation: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string => {
  if (count === 1) {
    return getTranslation(
      "bulk-delete-success-message",
      "1 Customer successfully deleted",
      { count },
    );
  }
  return getTranslation(
    "bulk-delete-success-message_plural",
    "{{count}} Customers successfully deleted",
    { count },
  );
};

const useCustomerList = (opts?: { defaultStatus?: string }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("customer");

  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState(opts?.defaultStatus ?? "");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [resultModal, setResultModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    icon: "success" | "danger" | "warning" | "info";
  }>({
    open: false,
    title: "",
    message: "",
    icon: "success",
  });
  const [bulkAction, setBulkAction] = useState("");
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<CustomerRow | null>(null);
  const [statusToggleRow, setStatusToggleRow] = useState<CustomerRow | null>(null);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [isSubmittingBulkAction, setIsSubmittingBulkAction] = useState(false);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);

  const [isExportOpen, setIsExportOpen] = useState(false);
  const [fileFormat, setFileFormat] = useState("xlsx");
  const [exportType, setExportType] = useState("all");

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [importValidationErrors, setImportValidationErrors] = useState<CustomerV1CustomerImportError[]>([]);

  const exportFileFormatOptions = [
    { label: "Excel (.xlsx)", value: "xlsx" },
    { label: "CSV (.csv)", value: "csv" },
  ];

  const exportTypeOptions = [
    { label: "All Data", value: "all" },
    { label: "Selected Data", value: "selected" },
  ];

  const {
    data: apiResponse,
    isLoading,
    refetch,
  } = useCustomerServiceV1ListCustomersQuery(
    {
      page: currentPage,
      limit: rowsPerPage,
      search: searchValue || undefined,
      status: statusFilter || undefined,
      sortBy: sortState ? sortByMap[sortState.columnId] : undefined,
      order: sortState?.direction,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: draftResponse } = useCustomerServiceV1ListCustomersQuery({
    page: 1,
    limit: 1,
    status: "Draft",
  });

  const [bulkUpdateCustomerStatus] = useCustomerServiceV1BulkUpdateCustomerStatusMutation();
  const [bulkDeleteCustomer] = useCustomerServiceV1BulkDeleteCustomerMutation();
  const [exportCustomers, { isLoading: isExporting }] = useCustomerServiceV1ExportCustomersMutation();
  const [importCustomers, { isLoading: isImporting }] = useCustomerServiceV1ImportCustomersMutation();

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: "Customer",
        hasBackButton: false,
      }),
    );
  }, [dispatch]);



  const tableData: CustomerRow[] = useMemo(() => {
    if (!apiResponse?.customer) {
      return [];
    }
    return apiResponse.customer.map(mapCustomerItemToRow);
  }, [apiResponse]);

  const totalPages = apiResponse?.metadata?.total_page ?? 1;

  const getTranslation = (
    key: string,
    fallback: string,
    options?: Record<string, unknown>,
  ) => {
    const val = t(key as never, options);
    if (val === key) {
      if (options && typeof options === "object") {
        let resolvedFallback = fallback;
        Object.entries(options).forEach(([k, v]) => {
          resolvedFallback = resolvedFallback.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), String(v));
        });
        return resolvedFallback;
      }
      return fallback;
    }
    return val;
  };

  const handleToggleStatusRequest = (row: CustomerRow) => {
    setStatusToggleRow(row);
    setShowStatusConfirmModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!statusToggleRow) {
      return;
    }

    try {
      const nextStatus = statusToggleRow.status === "active" ? "inactive" : "active";

      const payload: BulkUpdateCustomerStatusApiArg = {
        customerV1BulkUpdateCustomerStatusReq: {
          ids: [statusToggleRow.id],
          status: nextStatus,
        },
      };

      await bulkUpdateCustomerStatus(payload).unwrap();

      setResultModal({
        open: true,
        title:
          nextStatus === "active"
            ? getTranslation("bulk-activate-success-title", "Successfully Activated!")
            : getTranslation("bulk-deactivate-success-title", "Successfully Deactivated!"),
        message:
          nextStatus === "active"
            ? getTranslation("toast-activate-success", "{{name}} successfully activated", {
                name: statusToggleRow.CustomerName,
              })
            : getTranslation("toast-deactivate-success", "{{name}} successfully deactivated", {
                name: statusToggleRow.CustomerName,
              }),
        icon: "success",
      });

      setStatusToggleRow(null);
      setShowStatusConfirmModal(false);
      refetch();
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleDeleteRequest = (row: CustomerRow) => {
    if (row.status === "active") {
      toast.error(
        getTranslation(
          "toast-active-customer-cannot-delete",
          "Active customer cannot be deleted",
        ),
      );
      return;
    }
    setDeleteRow(row);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow || deleteRow.status === "active") {
      return;
    }

    try {
      const payload: BulkDeleteCustomerApiArg = {
        ids: [deleteRow.id],
      };

      const deleteName = deleteRow.CustomerName;
      await bulkDeleteCustomer(payload).unwrap();

      setResultModal({
        open: true,
        title: getTranslation("bulk-delete-success-title", "Successfully Deleted!"),
        message: getTranslation("toast-delete-success", "{{name}} successfully deleted", { name: deleteName }),
        icon: "success",
      });

      setDeleteRow(null);
      setShowDeleteConfirmModal(false);
      refetch();
    } catch (error) {
      errorHandler(error);
    }
  };

  const selectedStatuses = useMemo(() => {
    const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
    return Array.from(new Set(selectedRows.map((row) => row.status)));
  }, [selectedKeys, tableData]);

  const bulkActionOptions = useMemo(() => {
    if (selectedKeys.length === 0) return [];

    const hasActive = selectedStatuses.includes("active");
    const hasInactive = selectedStatuses.includes("inactive");

    if (selectedStatuses.length === 1) {
      if (hasActive) {
        return [
          {
            label: t("btn-deactivate") || getTranslation("btn-deactivate", "Deactivate"),
            value: "inactive",
          },
        ];
      }
      if (hasInactive) {
        return [
          {
            label: t("btn-activate") || getTranslation("btn-activate", "Activate"),
            value: "active",
          },
          {
            label: t("btn-delete") || getTranslation("btn-delete", "Delete"),
            value: "delete",
          },
        ];
      }
    }

    return [];
  }, [selectedKeys.length, selectedStatuses, t, getTranslation]);

  const effectiveBulkAction = bulkActionOptions.some((opt) => opt.value === bulkAction) ? bulkAction : "";

  const selectedVendorNames = useMemo(() => {
    return selectedKeys
      .map((key) => {
        const row = tableData.find((r) => r.id === key);
        return row ? row.CustomerName : "";
      })
      .filter(Boolean);
  }, [selectedKeys, tableData]);

  const handleApplyBulkAction = () => {
    if (selectedKeys.length === 0 || !bulkAction) {
      return;
    }
    if (selectedStatuses.length > 1) {
      return;
    }
    if (bulkAction === "active" && selectedStatuses.includes("active")) {
      return;
    }
    if (bulkAction === "inactive" && selectedStatuses.includes("inactive")) {
      return;
    }
    if (bulkAction === "delete") {
      const hasActiveCustomer = selectedKeys.some((key) => {
        const row = tableData.find((r) => r.id === key);
        return row?.status === "active";
      });
      if (hasActiveCustomer) {
        toast.error(
          getTranslation(
            "toast-cannot-delete-customer-status",
            "Only inactive customers can be deleted",
          ),
        );
        return;
      }
    }
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    if (selectedKeys.length === 0 || !bulkAction) {
      return;
    }

    if (bulkAction === "delete") {
      const hasActiveCustomer = selectedKeys.some((key) => {
        const row = tableData.find((r) => r.id === key);
        return row?.status === "active";
      });
      if (hasActiveCustomer) {
        toast.error(
          getTranslation(
            "toast-cannot-delete-customer-status",
            "Only inactive customers can be deleted",
          ),
        );
        return;
      }
    }

    try {
      setIsSubmittingBulkAction(true);
      const count = selectedKeys.length;

      if (bulkAction === "active" || bulkAction === "inactive") {
        const payload: BulkUpdateCustomerStatusApiArg = {
          customerV1BulkUpdateCustomerStatusReq: {
            ids: selectedKeys.map(String),
            status: bulkAction,
          },
        };

        await bulkUpdateCustomerStatus(payload).unwrap();

        const message = getBulkStatusUpdateSuccessMessage(
          bulkAction,
          count,
          getTranslation,
        );

        setResultModal({
          open: true,
          title:
            bulkAction === "active"
              ? getTranslation("bulk-activate-success-title", "Successfully Activated!")
              : getTranslation("bulk-deactivate-success-title", "Successfully Deactivated!"),
          message,
          icon: "success",
        });
      }

      if (bulkAction === "delete") {
        const payload: BulkDeleteCustomerApiArg = {
          ids: selectedKeys.map(String),
        };

        await bulkDeleteCustomer(payload).unwrap();

        const message = getBulkDeleteSuccessMessage(count, getTranslation);

        setResultModal({
          open: true,
          title: getTranslation("bulk-delete-success-title", "Successfully Deleted!"),
          message,
          icon: "success",
        });
      }

      setSelectedKeys([]);
      setBulkAction("");
      setShowBulkConfirmModal(false);
      refetch();
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSubmittingBulkAction(false);
    }
  };

  const columns: Columns<CustomerRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
        sticky: "left",
      },
      {
        id: "customerName",
        fieldId: "CustomerName",
        label: t("col-customer"),
        sortable: true,
        render: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-text-primary">{row.CustomerName}</span>
            {row.vendorCode && row.vendorCode !== "-" && (
              <span className="text-xs text-greyScale-60">{row.vendorCode}</span>
            )}
          </div>
        ),
      },
      {
        id: "legalName",
        fieldId: "legalName",
        label: t("col-legal-name"),
        sortable: true,
      },
      {
        id: "officialPhone",
        fieldId: "officialPhone",
        label: t("col-official-phone"),
        sortable: true,
      },
      ...(opts?.defaultStatus === "draft"
        ? []
        : [
            {
              id: "status",
              fieldId: "status" as const,
              label: t("col-status"),
              sortable: true,
              render: (row: CustomerRow) => (
                <div className="inline-flex items-center gap-2 text-sm">
                  <span
                    className={`h-3 w-3 rounded-full ${row.status === "active"
                      ? "bg-emerald-500 border-2 border-emerald-100"
                      : "bg-greyScale-60 border-2 border-greyScale-90"
                      }`}
                  />
                  <span className="capitalize">{row.status}</span>
                </div>
              ),
            },
          ]),
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "150px",
        dragDisabled: true,
        sticky: "right",
        render: (row) => (
          <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() =>
                navigate(
                  opts?.defaultStatus === "draft"
                    ? `/master-data/customer/draft/${row.id}/edit`
                    : `/master-data/customer/${row.id}/edit`,
                )
              }
            >
              {t("btn-edit")}
            </Button>

            <ActionMenu
              items={[
                ...(opts?.defaultStatus === "draft"
                  ? []
                  : [
                      {
                        label:
                          row.status === "active"
                            ? getTranslation("btn-deactivate", "Deactivate")
                            : getTranslation("btn-activate", "Activate"),
                        icon: (
                          <Icon
                            name={row.status === "active" ? "toggle-off" : "toggle-on"}
                            size={14}
                          />
                        ),
                        onClick: () => handleToggleStatusRequest(row),
                      },
                    ]),
                ...(opts?.defaultStatus === "draft" || row.status !== "active"
                  ? [
                      {
                        label: getTranslation("btn-delete", "Delete"),
                        icon: <Icon name="trash" size={14} />,
                        onClick: () => handleDeleteRequest(row),
                        variant: "danger" as const,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        ),
      },
    ],
    [t, navigate, opts?.defaultStatus],
  );

  const handleOpenImportModal = () => {
    setIsImportOpen(true);
  };

  const handleCloseImportModal = () => {
    setIsImportOpen(false);
    setSelectedImportFile(null);
    setImportValidationErrors([]);
  };

  const handleSelectImportFile = (file: File | null) => {
    if (!file) {
      return;
    }

    const allowedExtensions = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!allowedExtensions.includes(file.type)) {
      toast.error("Only .xlsx and .xls files are allowed");
      return;
    }

    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      toast.error("Maximum file size is 10 MB");
      return;
    }

    setSelectedImportFile(file);
    setImportValidationErrors([]);
  };

  const handleRemoveImportFile = () => {
    setSelectedImportFile(null);
  };

  const handleSubmitImport = async () => {
    try {
      if (!selectedImportFile) {
        toast.error("Please select a file");
        return;
      }

      const formData = new FormData();
      formData.append("file", selectedImportFile);

      const response = await importCustomers({
        // as any is required because generated schema expects ApiHttpBody while upload uses FormData
        apiHttpBody: formData as any,
      }).unwrap();

      if (response.success === false && response.errors) {
        setImportValidationErrors(response.errors);
        toast.error("Import validation failed");
        return;
      }

      toast.success(response.message || "Customers imported successfully");
      handleCloseImportModal();
      refetch();
    } catch (error: any) {
      const backendErrors = error?.data?.errors;

      if (Array.isArray(backendErrors)) {
        setImportValidationErrors(backendErrors);
      }

      errorHandler(error);
    }
  };
  const downloadTemplate = () => {
    if (env.CUSTOMER_IMPORT_TEMPLATE_URL) {
      window.open(env.CUSTOMER_IMPORT_TEMPLATE_URL, "_blank");
    } else {
      toast.error("Template URL is not configured");
    }
  };
  const vendorImport = {
    isOpen: isImportOpen,
    selectedFile: selectedImportFile,
    validationErrors: importValidationErrors,
    isLoading: isImporting,
    openModal: handleOpenImportModal,
    closeModal: handleCloseImportModal,
    onSelectFile: handleSelectImportFile,
    removeFile: handleRemoveImportFile,
    submitImport: handleSubmitImport,
    downloadTemplate,
  };

  const handleExportSubmit = async () => {
    if (!fileFormat) {
      toast.error("Please select file format");
      return;
    }

    if (!exportType) {
      toast.error("Please select export type");
      return;
    }

    if (exportType === "selected" && selectedKeys.length === 0) {
      toast.error("No customer selected");
      return;
    }

    try {
      const payload: CustomerV1ExportCustomersReq = {
        format: fileFormat,
        scope: exportType === "selected" ? "selected" : "all",
        filters: {
          page: currentPage,
          limit: rowsPerPage,
        },
      };

      if (exportType === "selected") {
        payload.ids = selectedKeys.map(String);
      }

      const response = await exportCustomers({
        customerV1ExportCustomersReq: payload,
      }).unwrap();

      const url = window.URL.createObjectURL(response);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = `customers.${fileFormat}`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);

      toast.success("Export success");
      setIsExportOpen(false);
      setFileFormat("xlsx");
      setExportType("all");
    } catch (error) {
      errorHandler(error);
    }
  };

  return {
    columns,
    currentPage,
    deleteRow,
    paginatedRows: tableData,
    rowsPerPage,
    searchValue,
    selectedKeys,
    bulkAction: effectiveBulkAction,
    bulkActionOptions,
    sortState,
    statusFilter,
    showDeleteConfirmModal,
    isLoading,
    isExporting,
    isSubmittingBulkAction,
    totalPages,
    totalSelected: selectedKeys.length,
    totalVisibleData: apiResponse?.metadata?.total ?? 0,
    draftCount: draftResponse?.metadata?.total ?? 0,
    vendorImport,
    t,
    setCurrentPage: (page: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setCurrentPage(page);
    },
    setRowsPerPage: (rows: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setRowsPerPage(rows);
    },
    setSearchValue: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setSearchValue(val);
    },
    setSelectedKeys,
    setBulkAction,
    setShowDeleteConfirmModal,
    setSortState: (sort: TableSortState | ((prev: TableSortState) => TableSortState)) => {
      setSelectedKeys([]);
      setSortState(sort);
    },
    setStatusFilter: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setStatusFilter(val);
    },
    onAddVendor: () => navigate(addCustomerPageRouteName),
    onDraft: () => navigate("/master-data/customer/draft"),
    onExport: () => {
      setExportType(selectedKeys.length > 0 ? "selected" : "all");
      setIsExportOpen(true);
    },
    isExportOpen,
    setIsExportOpen,
    fileFormat,
    setFileFormat,
    exportType,
    setExportType,
    exportFileFormatOptions,
    exportTypeOptions,
    handleExportSubmit,
    resultModal,
    setResultModal,
    statusToggleRow,
    showStatusConfirmModal,
    setShowStatusConfirmModal,
    onConfirmToggleStatus: handleConfirmToggleStatus,
    getTranslation,
    onImport: vendorImport.openModal,
    onConfirmDelete: handleConfirmDelete,
    handleApplyBulkAction,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    selectedVendorNames,
    onConfirmBulkStatusUpdate: handleConfirmBulkAction,
    onRowClick: (row: CustomerRow) => navigate(detailCustomerPageRouteName(row.id)),
  };
};

export default useCustomerList;
export type { CustomerRow };
